import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import sellerApi from "@/lib/sellerApi";
import { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";

export default function SellerForgotPassword() {
  usePageMeta("Reset Seller Password", "Reset your NICEE seller account password.");
  const navigate = useNavigate();
  const [form, setForm] = useState({ mobile: "", email: "", new_password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await sellerApi.post("/seller/forgot-password", form);
      toast.success(data.message || "Password reset successful");
      navigate("/seller/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl bg-white p-8 shadow-lift">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-ink">
          <KeyRound className="h-6 w-6" />
        </span>
        <h1 className="mt-5 text-center font-heading text-2xl font-bold text-ink">Reset Password</h1>
        <p className="mt-1 text-center text-sm text-ink-soft">Verify your registered mobile & email to set a new password</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-ink-soft">Mobile Number</Label>
            <Input value={form.mobile} onChange={set("mobile")} placeholder="Registered mobile" className="mt-1.5 rounded-xl" required data-testid="seller-forgot-mobile" />
          </div>
          <div>
            <Label className="text-ink-soft">Email Address</Label>
            <Input type="email" value={form.email} onChange={set("email")} placeholder="Registered email" className="mt-1.5 rounded-xl" required data-testid="seller-forgot-email" />
          </div>
          <div>
            <Label className="text-ink-soft">New Password</Label>
            <Input type="password" value={form.new_password} onChange={set("new_password")} placeholder="At least 6 characters" className="mt-1.5 rounded-xl" required data-testid="seller-forgot-password" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="seller-forgot-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="seller-forgot-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-ink-soft">
          <Link to="/seller/login" className="font-semibold text-brand hover:underline">Back to login</Link>
        </p>
      </motion.div>
    </div>
  );
}
